import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { UserProvider } from "@/contexts/UserContext";
import { lazy, Suspense } from "react";

// Eager-loaded (critical path)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import CompleteProfile from "./pages/auth/CompleteProfile";

// Protected routes
import { VenueProtectedRoute, CoachProtectedRoute, AppProtectedRoute, AdminProtectedRoute } from "./components/ProtectedRoute";

// Lazy-loaded layouts
const VenueLayout = lazy(() => import("./layouts/VenueLayout"));
const CoachLayout = lazy(() => import("./layouts/CoachLayout"));
const AppLayout = lazy(() => import("./layouts/AppLayout"));

// Lazy-loaded pages
const Pricing = lazy(() => import("./pages/Pricing"));
const JoinInvite = lazy(() => import("./pages/auth/JoinInvite"));

// Venue pages
const VenueDashboard = lazy(() => import("./pages/venue/Dashboard"));
const VenueSettings = lazy(() => import("./pages/venue/Settings"));
const VenueSpaces = lazy(() => import("./pages/venue/Spaces"));
const VenueBookings = lazy(() => import("./pages/venue/Bookings"));
const VenueCoaches = lazy(() => import("./pages/venue/Coaches"));
const VenueAnalytics = lazy(() => import("./pages/venue/Analytics"));
const VenueConfig = lazy(() => import("./pages/venue/Config"));
const VenueCategories = lazy(() => import("./pages/venue/Categories"));

// Coach pages
const CoachDashboard = lazy(() => import("./pages/coach/Dashboard"));
const CoachClasses = lazy(() => import("./pages/coach/Classes"));
const CoachCalendar = lazy(() => import("./pages/coach/Calendar"));
const CoachStudents = lazy(() => import("./pages/coach/Students"));
const CoachEarnings = lazy(() => import("./pages/coach/Earnings"));
const CoachSettings = lazy(() => import("./pages/coach/Settings"));
const CoachAttendance = lazy(() => import("./pages/coach/Attendance"));
const CoachCategories = lazy(() => import("./pages/coach/Categories"));
const CoachPublicProfile = lazy(() => import("./pages/coach/PublicProfile"));

// App pages
const AppHome = lazy(() => import("./pages/app/Home"));
const VenueDetail = lazy(() => import("./pages/app/VenueDetail"));
const MyBookings = lazy(() => import("./pages/app/MyBookings"));
const AppProfile = lazy(() => import("./pages/app/Profile"));
const AppClasses = lazy(() => import("./pages/app/Classes"));
const AppProgress = lazy(() => import("./pages/app/Progress"));
const AppAchievements = lazy(() => import("./pages/app/Achievements"));
const AppPacks = lazy(() => import("./pages/app/Packs"));
const AppCoaches = lazy(() => import("./pages/app/Coaches"));
const CoachClassesPlayer = lazy(() => import("./pages/app/CoachClasses"));

// Admin pages
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));

const LazyFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <UserProvider>
        <Suspense fallback={<LazyFallback />}>
        <Routes>
          {/* Landing page */}
          <Route path="/" element={<Index />} />

          {/* Public coach profile - BEFORE protected routes */}
          <Route path="/coach/:coachId/public" element={<CoachPublicProfile />} />

          {/* Public pages */}
          <Route path="/pricing" element={<Pricing />} />

          {/* Auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route path="/join/:token" element={<JoinInvite />} />

          {/* Venue Management Portal */}
          <Route
            path="/venue"
            element={
              <VenueProtectedRoute>
                <VenueLayout />
              </VenueProtectedRoute>
            }
          >
            <Route path="dashboard" element={<VenueDashboard />} />
            <Route path="settings" element={<VenueSettings />} />
            <Route path="spaces" element={<VenueSpaces />} />
            <Route path="bookings" element={<VenueBookings />} />
            <Route path="coaches" element={<VenueCoaches />} />
            <Route path="categories" element={<VenueCategories />} />
            <Route path="analytics" element={<VenueAnalytics />} />
            <Route path="config" element={<VenueConfig />} />
          </Route>

          {/* Coach Portal */}
          <Route
            path="/coach"
            element={
              <CoachProtectedRoute>
                <CoachLayout />
              </CoachProtectedRoute>
            }
          >
            <Route path="dashboard" element={<CoachDashboard />} />
            <Route path="classes" element={<CoachClasses />} />
            <Route path="calendar" element={<CoachCalendar />} />
            <Route path="attendance" element={<CoachAttendance />} />
            <Route path="students" element={<CoachStudents />} />
            <Route path="earnings" element={<CoachEarnings />} />
            <Route path="categories" element={<CoachCategories />} />
            <Route path="settings" element={<CoachSettings />} />
          </Route>

          {/* User App */}
          <Route
            path="/app"
            element={
              <AppProtectedRoute>
                <AppLayout />
              </AppProtectedRoute>
            }
          >
            <Route path="home" element={<AppHome />} />
            <Route path="venue/:id" element={<VenueDetail />} />
            <Route path="bookings" element={<MyBookings />} />
            <Route path="profile" element={<AppProfile />} />
            <Route path="classes" element={<AppClasses />} />
            <Route path="progress" element={<AppProgress />} />
            <Route path="achievements" element={<AppAchievements />} />
            <Route path="packs" element={<AppPacks />} />
            <Route path="coaches" element={<AppCoaches />} />
            <Route path="coach/:coachId/classes" element={<CoachClassesPlayer />} />
          </Route>

          {/* Admin Portal */}
          <Route
            path="/admin"
            element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }
          >
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="verifications" element={<AdminUsers />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </UserProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
